import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiBriefcase } from 'react-icons/fi';
import ClientModal from '../../../components/ClientModal';
import ConfirmModal from '../../../components/ConfirmModal';
import api from '../../../services/api';

const AdminClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Failed to load clients. Please try again.');
      setLoading(false);
    }
  };
  
  const handleAddClient = () => {
    setCurrentClient(null);
    setShowClientModal(true);
  };
  
  const handleEditClient = (client) => {
    setCurrentClient(client);
    setShowClientModal(true);
  };
  
  const handleDeleteClient = (client) => {
    setCurrentClient(client);
    setShowDeleteModal(true);
  };
  
  const handleSaveClient = async (clientData) => {
    try {
      if (currentClient) {
        // Update existing client
        const response = await api.put(`/clients/${currentClient.id}`, clientData);
        setClients(clients.map(client => client.id === currentClient.id ? response.data : client));
      } else {
        // Create new client
        const response = await api.post('/clients', clientData);
        setClients([...clients, response.data]);
      }
      setShowClientModal(false);
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/clients/${currentClient.id}`);
      setClients(clients.filter(client => client.id !== currentClient.id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client: ' + (error.response?.data?.message || error.message));
    }
  };
  



    


  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Client Management</h1>
        <div className="text-center py-8">Loading clients...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Client Management</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchClients} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Client Management</h1>
        <button
          onClick={handleAddClient}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" /> Add Client
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading clients...</div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={fetchClients} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow p-6">
          <FiBriefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients available</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first client</p>
          <button
            onClick={handleAddClient}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center mx-auto"
          >
            <FiPlus className="mr-2" /> Add Client
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.map(client => (
                <tr key={client.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-600 font-medium">
                          {((client.first_name || '')[0] || '') + ((client.last_name || '')[0] || '')}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {`${client.first_name || ''} ${client.last_name || ''}`.trim() || 'N/A'}
                        </div>
                        {client.address && (
                          <div className="text-xs text-gray-500">{client.address}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.email && (
                      <div className="text-sm text-gray-500">{client.email}</div>
                    )}
                    {client.phone && (
                      <div className="text-sm text-gray-500">{client.phone}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {client.notes ? (client.notes.length > 30 ? client.notes.substring(0, 30) + '...' : client.notes) : 'No notes'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      <FiEdit2 className="inline" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FiTrash2 className="inline" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showClientModal && (
        <ClientModal
          client={currentClient}
          onClose={() => setShowClientModal(false)}
          onSave={handleSaveClient}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Client"
          message={`Are you sure you want to delete ${currentClient?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default AdminClientsPage;